import { type DriveFile } from '@/shared/DriveTypes';
import { OpenInNew } from '@mui/icons-material';
import { useRef } from 'react';
import { fadeInSlide, fadeInSlideRight } from '~/app.css';
import { base } from '~/theme.css';
import { editURLs, LEGACY } from '~/utils/constants';
import { useKeyPress } from '~/utils/hooks/hooks';
import { isMarkdownLink } from '~/utils/isMarkdownLink';
import registerComponent from '~/utils/registerComponent';
import { useTheme } from '~/utils/store';
import { cx, getMimeTypeNameFromFile, isAPDF, isAShortcut, removeURLParams } from '~/utils/utils';
import { EditOutlinedAlt, ViewOutlinedAlt } from '../icons';
import * as styles from './Button.css';

export const Edit = ({
  href,
  text = 'Edit',
  onClick,
  inline = false,
  editing = false,
  animation,
  keypress,
}: {
  href: string;
  text?: 'View' | 'Edit';
  onClick?: ((e: React.MouseEvent) => void) | undefined;
  inline?: boolean;
  editing?: boolean;
  animation?: 'left' | 'right' | 'none';
  keypress?: boolean;
}) => {
  // Keyboard support
  const editButtonRef = useRef<HTMLAnchorElement>(null);
  useKeyPress('e', () => {
    if (!keypress) return;
    editButtonRef.current?.click();
  });
  const theme = useTheme();

  return (
    <a
      {...(inline
        ? {
            role: 'button',
          }
        : {
            target: '_blank',
            rel: 'noreferrer',
            href,
          })}
      ref={editButtonRef}
      onClick={onClick}
      className={cx(styles.buttonLight, {
        [fadeInSlideRight]: animation === 'right',
        [fadeInSlide]: animation === 'left',
      })}
      title={(inline && editing ? 'View' : text) + ' (e)'}
      {...(LEGACY && { 'data-test-id': 'button-edit' })}
      {...(theme === 'dark' && {
        style: {
          background: editing ? base.white10 : undefined,
          color: editing ? base.gray140 : undefined,
        },
      })}
    >
      {inline && editing ? 'View' : text}
      {inline ? (
        editing ? (
          <ViewOutlinedAlt className={cx(styles.openInNewIcon, styles.viewIcon)} />
        ) : (
          <EditOutlinedAlt className={cx(styles.openInNewIcon, styles.editIcon)} />
        )
      ) : (
        <OpenInNew className={styles.openInNewIcon} />
      )}
    </a>
  );
};

const getText = (file: DriveFile, markdown = isMarkdownLink(file)) => {
  if (markdown || isAPDF(file)) return 'View';
  if (file.capabilities?.canEdit) return 'Edit';
  return 'View';
};

const getEditUrl = (file: DriveFile) => {
  if (isAShortcut(file)) {
    const { targetId, targetMimeType } = file.shortcutDetails;
    const mimeTypeName = getMimeTypeNameFromFile({ ...file, mimeType: targetMimeType } as DriveFile);
    const baseUrl = mimeTypeName ? editURLs[mimeTypeName as keyof typeof editURLs] : undefined;
    if (baseUrl) {
      return `${baseUrl}${targetId}/edit`;
    }
  }
  return file.webViewLink;
};

const EditButton = ({
  file,
  wiki,
  animation = 'none',
  keypress = false,
  editing = false,
  onClick,
}: {
  file: DriveFile | undefined;
  wiki: DriveFile | undefined;
  animation?: 'left' | 'right' | 'none';
  keypress?: boolean;
  editing?: boolean;
  onClick?: ((e: React.MouseEvent) => void) | undefined;
}) => {
  if (!file) return null;
  const markdown = isMarkdownLink(file);
  const text = getText(file, markdown);
  const inline = wiki?.properties?.showInlineEditing === 'true';
  const webViewLink = getEditUrl(file);

  if (!webViewLink) return null;
  const url = markdown?.url ?? removeURLParams(webViewLink);
  const href = url;
  return (
    <Edit
      href={href}
      text={text}
      inline={markdown ? false : inline}
      editing={editing}
      animation={animation}
      keypress={keypress}
      onClick={onClick}
    />
  );
};

if (LEGACY) {
  registerComponent(Edit, 'wc-edit', {
    text: 'string',
    href: 'string',
    onClick: 'function',
    inline: 'boolean',
    animation: 'boolean',
    keypress: 'boolean',
  });
}

export default EditButton;
